import SaveIcon from '@mui/icons-material/Save';
import Button from '@mui/material/Button';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useMemo, useState } from 'react';

import { SetAiExtractionContext } from '#screens/ai-workbench/ai-extraction-context.js';
import { NormalizedSignalDriveModesEditor } from '#screens/normalized-signal-drive-modes/normalized-signal-drive-modes-editor.js';
import { SignalDriveModeTable } from '#screens/signal-drive-mode-table/signal-drive-mode-table.js';
import { useTRPC } from '#src/trpc.js';
import { useAutoSave } from '#utils/use-auto-save/use-auto-save.js';
import { getSignalDriveModesContextValue } from './signal-drive-modes-context.js';

import type { HilSignal } from '@cadstrom/backend/shared';
import type { Wizard } from '@cadstrom/cadstrom-shared';
import type { ModeBase, SignalDriveModeDefinition } from '@cadstrom/sigma-engine-shared';

export const Route = createFileRoute(
	'/_authorization-gate/package-store/$packageFamilyId/packages/$packageId/signal-drive-modes/',
)({
	staticData: {
		navigation: {
			label: 'Package: Signal drive modes',
			keywords: ['netdrive'],
		},
	},
	loader: ({ context: { queryClient, getPackageOptions } }) => {
		void queryClient.prefetchQuery(getPackageOptions);
		const cached = queryClient.getQueryData(getPackageOptions.queryKey);
		return { pkg: cached?.pkg };
	},
	// The draft below is per package, but `useAutoSave` only resyncs it to a newly selected package
	// in a post-render effect. A mount that outlived a package switch would therefore render the
	// normalized editor — which seeds itself once, on mount — against the previous package's draft,
	// and lower and save those thresholds onto the package now on screen. Remounting on the id
	// instead keeps the draft and both editors on one package for their whole lifetime.
	remountDeps: ({ params }) => ({ packageId: params.packageId }),
	head: ({ loaderData }) => ({
		meta: [
			{
				title:
					(loaderData?.pkg ? loaderData.pkg.manufacturerPartNumber + ': ' : '') +
					'Signal drive modes • Package Editor • Cadstrom',
			},
		],
	}),
	component: SignalDriveModesTab,
});

/** The draft the tab saves: the normalized editor rewrites modes alongside the signals. */
interface SignalDriveModesDraft {
	signals: HilSignal[];
	modes: ModeBase[];
	/**
	 * The normalized editor's table as captured, saved alongside what it lowers to so the next visit
	 * opens on what the operator stated. Undefined until they touch that editor — a package resolved
	 * elsewhere has none, and the legacy per-signal editor never produces one.
	 */
	driveModeTable?: Wizard.NormalizedSignalDriveModesFormState;
}

/**
 * Which drive-modes editor the tab shows: the threshold-centric normalized
 * editor, or the per-signal drive-mode table that preceded it.
 */
type SignalDriveModesEditorMode = 'normalized' | 'legacy';

function SignalDriveModesTab() {
	const queryClient = useQueryClient();
	const { getPackageOptions } = Route.useRouteContext();
	const trpc = useTRPC();

	const {
		data: { pkg },
	} = useSuspenseQuery(getPackageOptions);

	const [editorMode, setEditorMode] = useState<SignalDriveModesEditorMode>('normalized');

	const { mutateAsync: updatePackage, isPending: updatePackageIsPending } = useMutation(
		trpc.package.update.mutationOptions({
			onSuccess: (res) => {
				// Merge rather than replace: `isConfidential` is derived from the package family's owner and
				// is not in the mutation response, so overwriting the entry would drop it and hide the badge.
				queryClient.setQueryData(getPackageOptions.queryKey, (previous) =>
					previous === undefined ? undefined : { ...previous, pkg: res.pkg },
				);

				setServerDraft({
					signals: res.pkg.signals,
					modes: res.pkg.modes,
					driveModeTable: res.pkg.metadata.signalDriveModeTable,
				});
			},
		}),
	);

	const {
		draftState: draft,
		setDraftState: setDraft,
		setServerState: setServerDraft,
		isDirty,
		handleSave,
		saveAreaContainerRef,
	} = useAutoSave({
		initialState: {
			signals: pkg.signals,
			modes: pkg.modes,
			driveModeTable: pkg.metadata.signalDriveModeTable,
		} satisfies SignalDriveModesDraft,
		mutationKeys: trpc.package.update.mutationKey(),
		saveFn: async (draft) =>
			await updatePackage({
				id: pkg.id,
				data: {
					signals: {
						set: draft.signals,
					},
					modes: draft.modes,
					// Only written once the normalized editor has produced one, so saving from the legacy
					// per-signal editor leaves a previously captured table alone rather than erasing it.
					...(draft.driveModeTable === undefined ? {} : { metadata: { signalDriveModeTable: draft.driveModeTable } }),
				},
			}),
	});

	const draftSignals = draft.signals;
	const setDraftSignals = useCallback(
		(update: (signals: HilSignal[]) => HilSignal[]) => {
			setDraft((prev) => ({ ...prev, signals: update(prev.signals) }));
		},
		[setDraft],
	);

	const handleUpdateSignal = (signalId: string | number, supportedDriveModes: SignalDriveModeDefinition[]) => {
		setDraftSignals((prev) =>
			prev.map((signal) => (signal.id === signalId ? { ...signal, supportedDriveModes } : signal)),
		);
	};

	const handleReplaceDriveModes = (signalId: string | number, driveModes: SignalDriveModeDefinition[]) => {
		handleUpdateSignal(signalId, driveModes);
	};

	const handleAddDriveMode = (signalId: string | number, driveMode: SignalDriveModeDefinition) => {
		const signal = draftSignals.find((s) => s.id === signalId);
		if (!signal) {
			return;
		}

		const existingDriveModes = signal.supportedDriveModes;
		const newDriveModes = [...existingDriveModes, driveMode];

		handleUpdateSignal(signalId, newDriveModes);
	};

	const handleUpdateDriveMode = (
		signalId: string | number,
		driveModeIndex: number,
		driveMode: SignalDriveModeDefinition,
	) => {
		const signal = draftSignals.find((s) => s.id === signalId);
		if (!signal) {
			return;
		}

		const existingDriveModes = signal.supportedDriveModes;
		const newDriveModes = [...existingDriveModes];
		newDriveModes[driveModeIndex] = driveMode;

		handleUpdateSignal(signalId, newDriveModes);
	};

	const handleDeleteDriveMode = (signalId: string | number, driveModeIndex: number) => {
		const signal = draftSignals.find((s) => s.id === signalId);
		if (!signal) {
			return;
		}

		const existingDriveModes = signal.supportedDriveModes;
		const newDriveModes = existingDriveModes.filter((_, index) => index !== driveModeIndex);

		handleUpdateSignal(signalId, newDriveModes);
	};

	const handleUpdateSignals = useCallback(
		(updatedSignals: HilSignal[]) => {
			setDraftSignals((prev) =>
				prev.map((signal) => {
					const updatedSignal = updatedSignals.find((s) => s.id === signal.id);
					return updatedSignal ?
							{
								...signal,
								supportedDriveModes: updatedSignal.supportedDriveModes,
							}
						:	signal;
				}),
			);
		},
		[setDraftSignals],
	);

	const handleClearDriveModes = () => {
		setDraftSignals((prev) => prev.map((signal) => ({ ...signal, supportedDriveModes: [] })));
	};

	const extractionContextValue = useMemo(() => {
		return getSignalDriveModesContextValue({
			signals: draftSignals,
			modes: draft.modes,
			updateSignals: handleUpdateSignals,
		});
	}, [draftSignals, handleUpdateSignals, draft.modes]);

	return (
		<div ref={saveAreaContainerRef} className='flex min-h-0 grow basis-0 flex-col gap-2 px-3 py-2'>
			<div className='flex shrink-0 justify-between'>
				<ToggleButtonGroup
					exclusive
					value={editorMode}
					onChange={(_event, next: SignalDriveModesEditorMode | null) => {
						if (next !== null) {
							setEditorMode(next);
						}
					}}
					aria-label='Drive modes editor'
				>
					<ToggleButton value='normalized'>
						<span>Thresholds</span>
					</ToggleButton>
					<ToggleButton value='legacy'>
						<span>Per signal</span>
					</ToggleButton>
				</ToggleButtonGroup>

				<Button
					startIcon={<SaveIcon />}
					disabled={!isDirty}
					loading={updatePackageIsPending}
					onClick={() => void handleSave()}
				>
					<span>Save</span>
				</Button>
			</div>

			{editorMode === 'normalized' ?
				<NormalizedSignalDriveModesEditor
					signals={draftSignals}
					modes={draft.modes}
					differentialPairs={pkg.differentialPairs}
					// The draft's table rather than the package's, so switching to the per-signal editor and
					// back returns to what was captured instead of to the last save. Either can go stale
					// against drive modes the per-signal editor changed in between — only one of the two
					// views can be authoritative — but this at least does not discard unsaved work as well.
					initialFormState={draft.driveModeTable}
					onChange={({ signals, modes, formState }) =>
						setDraft((prev) => ({ ...prev, signals, modes, driveModeTable: formState }))
					}
				/>
			:	<SignalDriveModeTable
					signals={draftSignals}
					modes={draft.modes}
					onAddDriveMode={handleAddDriveMode}
					onUpdateDriveMode={handleUpdateDriveMode}
					onDeleteDriveMode={handleDeleteDriveMode}
					onReplaceDriveModes={handleReplaceDriveModes}
					onClearDriveModes={handleClearDriveModes}
					showSaveButton={{
						saveDisabled: !isDirty,
						onClickSave: handleSave,
						isSaving: updatePackageIsPending,
					}}
				/>
			}

			<SetAiExtractionContext value={extractionContextValue} />
		</div>
	);
}
